import React, { useCallback, useEffect, useState, useMemo, useRef } from "react";
// import Loader from "../../loader/Loader";
import { toast } from "react-toastify";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import {
  SET_PriceTypeConfig,
  selectPriceTypeConfig,
} from "../../../redux/IchthusSlice";
import { domain } from "../../../security";

import { ImPlus } from "react-icons/im";
import {
  FaTimes,
  FaSave,
  FaArrowRight,
  FaFileAlt,
  FaBoxOpen,
  FaMinus,
  FaCheck,
  FaEdit,
  FaPercentage,
  FaUser,
  FaMapMarkerAlt,
  FaSearch,
  FaTrash,
} from "react-icons/fa";
import {
  Search,
  MapPin,
  AlertCircle,
  Package,
  ChevronDown,
  Loader2,
  Settings2,
  SlidersHorizontal,
} from "lucide-react";
import { 
  TermsSelect 
} from "../Selections";
import AddSalesOrderAdvancedSearch from "../SalesOrders/SalesOrderModule/AddSalesOrderAdvancedSearch";
import OrderFinancialSummary from "../SalesOrders/SalesOrderModule/OrderFinancialSummary";
import { serviceCategories } from "../Inventory/InventoryModule/AllInventory";

// --- COMPACT AESTHETIC CLASSES ---
const inputClass =
  "block w-full rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm font-semibold text-gray-900 transition-all focus:border-indigo-500 focus:bg-white focus:ring-2 focus:ring-indigo-200 outline-none";
const labelClass =
  "mb-1 ml-0.5 block text-[10px] font-black uppercase tracking-widest text-gray-500";
const readOnlyBox =
  "flex items-center gap-3 p-2 bg-slate-50 rounded-lg border border-slate-100";

// --- PRICE TYPE DEFINITIONS ---
const ALL_PRICE_TYPES = [
  {
    configKey: "showVatInc",
    field: "vatInc",
    label: "VAT Inc",
    fullLabel: "VAT Inclusive",
    highlightClass:
      "bg-indigo-50 text-indigo-700 border border-indigo-200 font-bold",
    activeHeaderClass: "text-indigo-700 bg-indigo-50/50 font-extrabold",
  },
  {
    configKey: "showVatEx",
    field: "vatEx",
    label: "VAT Ex",
    fullLabel: "VAT Exclusive",
    highlightClass: "bg-blue-50 text-blue-700 border border-blue-200 font-bold",
    activeHeaderClass: "text-blue-700 bg-blue-50/50 font-extrabold",
  },
  {
    configKey: "showReseller",
    field: "reseller",
    label: "Reseller",
    fullLabel: "Reseller Price",
    highlightClass:
      "bg-violet-50 text-violet-700 border border-violet-200 font-bold",
    activeHeaderClass: "text-violet-700 bg-violet-50/50 font-extrabold",
  },
  {
    configKey: "showZeroRated",
    field: "zeroRated",
    label: "Zero Rated",
    fullLabel: "Zero Rated",
    highlightClass:
      "bg-amber-50 text-amber-700 border border-amber-200 font-bold",
    activeHeaderClass: "text-amber-700 bg-amber-50/50 font-extrabold",
  },
];

// --- HELPERS ---
const currencyFormatter = new Intl.NumberFormat("en-PH", {
  style: "currency",
  currency: "PHP",
});
const fmt = (val) => currencyFormatter.format(val ?? 0);

const computeDiscount = (qty, unitPrice, discountType, discountValue) => {
  const unitTotal = (parseFloat(qty) || 0) * (parseFloat(unitPrice) || 0);
  const val = parseFloat(discountValue) || 0;
  let discountAmount = 0;
  if (discountType === "%") {
    discountAmount = unitTotal * (Math.min(val, 100) / 100);
  } else {
    discountAmount = Math.min(val, unitTotal);
  }
  const lineTotal = Math.max(0, unitTotal - discountAmount);
  return { discountAmount, lineTotal };
};

const defaultDiscountFields = {
  discountType: "%",
  discountValue: 0,
  discountAmount: 0,
};

const calculateTotals = (items, shippingCost, hasVat, hasEwt) => {
  const subtotal = items.reduce(
    (acc, item) => acc + (parseFloat(item.lineTotal) || 0),
    0,
  );
  let vatAmt = 0,
    ewtAmt = 0,
    netOfVat = subtotal;
  if (hasVat) {
    netOfVat = subtotal / 1.12;
    vatAmt = netOfVat * 0.12;
  }
  if (hasEwt) {
    const taxBase = hasVat ? netOfVat : subtotal;
    ewtAmt = taxBase * 0.01;
  }
  const grandTotal = subtotal + (parseFloat(shippingCost) || 0) - ewtAmt;
  return { subtotal, grandTotal, vatAmt, ewtAmt, netOfVat };
};

// --- STEP INDICATOR ---
const StepIndicator = ({ step, isEditMode }) => {
  if (isEditMode) return null;
  return (
    <div className="flex items-center gap-2">
      {[
        { n: 1, label: "Select Products" },
        { n: 2, label: "Quote Details" },
      ].map((s, i) => (
        <React.Fragment key={s.n}>
          <div className="flex items-center gap-2">
            <div
              className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-black transition-all ${
                step === s.n
                  ? "bg-teal-600 text-white shadow-lg shadow-teal-200"
                  : step > s.n
                    ? "bg-green-500 text-white"
                    : "bg-gray-200 text-gray-400"
              }`}
            >
              {step > s.n ? <FaCheck size={9} /> : s.n}
            </div>
            <span
              className={`text-xs font-bold hidden sm:inline transition-colors ${
                step === s.n
                  ? "text-teal-600"
                  : step > s.n
                    ? "text-green-600"
                    : "text-gray-400"
              }`}
            >
              {s.label}
            </span>
          </div>
          {i < 1 && (
            <div
              className={`w-8 h-0.5 rounded-full transition-colors ${
                step > 1 ? "bg-green-400" : "bg-gray-200"
              }`}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

// --- PRODUCT ROW ---
const ProductRow = ({
  group,
  selectedItems,
  onAdd,
  onRemove,
  activePriceTypes,
  priceType,
  isMobile,
}) => {
  const [selectedVariantId, setSelectedVariantId] = useState(group[0].uniqueId);
  const activeItem =
    group.find((i) => i.uniqueId === selectedVariantId) || group[0];
  const qtyInSelection = selectedItems
    .filter((s) => s.uniqueId === activeItem.uniqueId)
    .reduce((sum, s) => sum + s.quantity, 0);

  const hasNoStock = activeItem.stockCount <= 0;
  const isPromo = activeItem.priceType === "PROMO";

  const renderVariantSelector = () =>
    group.length === 1 ? (
      <span className="text-gray-600 font-medium text-xs">
        {activeItem.uomName}
      </span>
    ) : (
      <select
        className="border border-gray-300 rounded-lg px-2 py-1 text-xs focus:outline-none focus:border-teal-400 bg-white cursor-pointer"
        value={activeItem.uniqueId}
        onChange={(e) => setSelectedVariantId(e.target.value)}
        onClick={(e) => e.stopPropagation()}
      >
        {group.map((v) => (
          <option key={v.uniqueId} value={v.uniqueId}>
            {v.uomName}
          </option>
        ))}
      </select>
    );

  const AddRemoveControls = () =>
    qtyInSelection > 0 ? (
      <>
        <div className="hidden items-center justify-center gap-1.5">
          <button
            onClick={() => onRemove(activeItem)}
            className="w-6 h-6 bg-gray-100 hover:bg-red-100 hover:text-red-600 text-gray-500 rounded-lg flex items-center justify-center transition-all"
          >
            <FaMinus size={9} />
          </button>
          <span className="text-sm font-black w-6 text-center text-teal-700">
            {qtyInSelection}
          </span>
          <button
            onClick={() => onAdd(activeItem)}
            className="w-6 h-6 bg-teal-600 hover:bg-teal-700 text-white rounded-lg flex items-center justify-center transition-all"
          >
            <ImPlus size={9} />
          </button>
        </div>
        <span className="text-white bg-green-500 text-xs rounded-lg px-2 py-1">ADDED</span>
      </>
    ) : (
      <button
        onClick={() => onAdd(activeItem)}
        className="p-2 rounded-lg transition-all text-gray-600 hover:bg-teal-50 hover:text-teal-700 active:scale-95"
      >
        <ImPlus size={15} />
      </button>
    );

  if (isMobile) {
    return (
      <div className="border border-gray-200 rounded-xl shadow-sm p-3 bg-white">
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
              <p className="font-bold text-xs text-gray-800 leading-tight">
                {activeItem.productName}
              </p>
              {isPromo && (
                <span className="bg-emerald-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded">
                  PROMO
                </span>
              )}
            </div>
            <p className="text-gray-400 text-[10px] font-mono">
              {activeItem.itemCode}
            </p>
          </div>
          {qtyInSelection > 0 && (
            <span className="text-white text-[10px] font-black px-2 py-0.5 rounded-full shrink-0 bg-teal-600">
              ×{qtyInSelection}
            </span>
          )}
        </div>
        <div className="flex flex-wrap gap-1.5 mb-2">
          {activePriceTypes.map(
            (pt) =>
              activeItem[pt.field] != null && (
                <span
                  key={pt.field}
                  className={`text-[10px] px-2 py-0.5 rounded font-semibold border ${
                    pt.field === priceType
                      ? pt.highlightClass
                      : "bg-gray-50 text-gray-500 border-gray-200"
                  }`}
                >
                  {pt.label}: {fmt(activeItem[pt.field])}
                </span>
              ),
          )}
        </div>
        <div className="flex items-center justify-between border-t border-gray-100 pt-2">
          <div className="flex items-center gap-2">
            {renderVariantSelector()}
            <span className="text-[10px] font-semibold text-gray-400">
              {activeItem.stockCount} in stock
            </span>
          </div>
          <AddRemoveControls />
        </div>
      </div>
    );
  }

  const isGoodsItem = !serviceCategories.includes(activeItem.categoryId)

  return (
    <tr className="border-b border-gray-100 hover:bg-gray-50/50 transition-colors">
      <td className="py-2 px-6">
        <p className="font-bold text-gray-800 text-sm">
          {activeItem.productName}
        </p>
        <p className="text-gray-400 text-[10px] font-mono">
          {activeItem.itemCode}
        </p>
      </td>
      <td className="py-2 px-6 text-center">
        {activeItem.brandName ?
        <>
          <span className="text-xs">{activeItem.brandName}</span>
        </>
        :
        <>
          <span className="text-xs italic text-gray-400">{"N/A"}</span>
        </>
        }
      </td>
      <td className="py-2 px-6 text-center">{renderVariantSelector()}</td>
      <td className="py-2 px-6 text-center">
        <span
          className={`px-2 py-0.5 rounded text-[10px] font-bold ${isPromo ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"}`}
        >
          {activeItem.priceType || "REG"}
        </span>
      </td>
      {activePriceTypes.map((pt) => (
        <td
          key={pt.field}
          className={`py-2 px-3 text-center ${pt.field === priceType ? "bg-teal-50/30" : ""}`}
        >
          {activeItem[pt.field] != null ? (
            <span
              className={`px-2.5 py-1 rounded-md text-xs border ${pt.field === priceType ? pt.highlightClass : "text-gray-600 border-transparent font-medium"}`}
            >
              {fmt(activeItem[pt.field])}
            </span>
          ) : (
            <span className="text-gray-200 text-xs">—</span>
          )}
        </td>
      ))}
      <td className="py-2 px-6 text-center">
        <span
          className={`px-2 py-0.5 rounded text-[11px] font-bold ${hasNoStock ? "bg-amber-100 text-amber-700 border border-amber-200" : "bg-gray-100 text-gray-600"}`}
        >
          {isGoodsItem ? activeItem.stockCount : "-"}
        </span>
      </td>
      <td className="py-2 px-6 text-center">
        <AddRemoveControls />
      </td>
    </tr>
  );
};

// --- DISCOUNT CELL ---
const DiscountCell = ({ item, index, onLineItemChange }) => {
  const unitTotal =
    (parseFloat(item.quantity) || 0) * (parseFloat(item.unitPrice) || 0);
  const hasDiscount = (parseFloat(item.discountValue) || 0) > 0;

  return (
    <div className="flex items-center gap-1">
      <select
        value={item.discountType || "%"}
        onChange={(e) =>
          onLineItemChange(index, "discountType", e.target.value)
        }
        className={`h-[30px] rounded-lg border text-[11px] font-black cursor-pointer outline-none transition-all px-1.5 appearance-none ${
          hasDiscount
            ? "border-rose-300 bg-rose-50 text-rose-700"
            : "border-gray-200 bg-gray-50 text-gray-500 hover:border-gray-300"
        }`}
      >
        <option value="%">%</option>
        <option value="AMOUNT">₱</option>
      </select>
      <input
        type="number"
        min="0"
        max={item.discountType === "%" ? 100 : unitTotal}
        step="0.01"
        value={item.discountValue || ""}
        placeholder="0"
        onChange={(e) =>
          onLineItemChange(index, "discountValue", e.target.value)
        }
        className={`w-20 h-[30px] rounded-lg border text-xs font-bold text-right pr-2 pl-2 outline-none transition-all ${
          hasDiscount
            ? "border-rose-300 bg-rose-50 text-rose-700 focus:ring-1 focus:ring-rose-200"
            : "border-gray-200 bg-gray-50 text-gray-600 focus:border-teal-300 focus:bg-white focus:ring-1 focus:ring-teal-100"
        }`}
      />
      {hasDiscount && (
        <span className="text-[9px] font-black text-rose-600 bg-rose-50 border border-rose-200 rounded px-1 py-0.5 whitespace-nowrap">
          -{fmt(item.discountAmount || 0)}
        </span>
      )}
    </div>
  );
};

// --- MAIN COMPONENT ---
const AddQuotationSales = ({ onClose, refreshData, qsData }) => {
  const dispatch = useDispatch();
  const priceTypeConfig = useSelector(selectPriceTypeConfig);

  const isEditMode = !!qsData;
  // const [step, setStep] = useState(isEditMode ? 2 : 1);
  const [step, setStep] = useState(2);
  const [isAdvancedSearchOpen, setIsAdvancedSearchOpen] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingBase, setIsFetchingBase] = useState(true);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [isConfigLoading, setIsConfigLoading] = useState(false);

  const [customers, setCustomers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [pricelists, setPricelists] = useState([]);

  const [priceType, setPriceType] = useState("");
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState("");
  const [selectedLocationName, setSelectedLocationName] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  // ── FORM STATE ────────────────────────────────────────────────────────────
  const [formData, setFormData] = useState(() => {
    if (isEditMode && qsData) {
      return {
        quoteNumber: qsData.quoteNumber || "Auto-Generated",
        date: qsData.date
          ? qsData.date.split("T")[0]
          : new Date().toISOString().split("T")[0],
        validityDate: qsData.validityDate
          ? qsData.validityDate.split("T")[0]
          : "",
        status: qsData.status || "Active",
        remarks: qsData.remarks || "",
        locationId: qsData.locationId || "",
        locationName: qsData.locationName || "",
        customerId: qsData.customerId ? String(qsData.customerId) : "",
        salesmanId: qsData.salesmanId ? String(qsData.salesmanId) : "",
        customerName: qsData.customerName || "",
        shippingAddress: qsData.shippingAddress || "",
        shippingCost: qsData.shippingCost || 0,
        hasVat: qsData.hasVat || false,
        hasEwt: qsData.hasEwt || false,
        vatAmount: qsData.vatAmount || 0,
        ewtAmount: qsData.ewtAmount || 0,
        vatableAmount: qsData.vatableAmount || 0,
        totalAmount: qsData.totalAmount || 0,
        quotationSalesProducts: (qsData.quotationSalesProducts || []).map(
          (item, index) => {
            const discountType = item.discountType || "%";
            const discountValue = item.discountValue || 0;
            const { discountAmount, lineTotal } = computeDiscount(
              item.quantity || 0,
              item.unitPrice || 0,
              discountType,
              discountValue,
            );
            return {
              ...item,
              lineNumber: index + 1,
              lineTotal: item.lineTotal != null ? item.lineTotal : lineTotal,
              conversionRate: item.conversionRate || 1,
              priceType: item.priceType || "N/A",
              discountType,
              discountValue,
              discountAmount: item.discountAmount ?? discountAmount,
            };
          },
        ),
      };
    }
    return {
      quoteNumber: "Auto-Generated",
      date: new Date().toISOString().split("T")[0],
      validityDate: "",
      status: "Active",
      remarks: "",
      locationId: "",
      locationName: "",
      customerId: "",
      salesmanId: "",
      customerName: "",
      shippingAddress: "",
      shippingCost: 0,
      hasVat: false,
      hasEwt: false,
      vatAmount: 0,
      ewtAmount: 0,
      vatableAmount: 0,
      totalAmount: 0,
      quotationSalesProducts: [],
    };
  });

  const totalSelectedQty = selectedItems.reduce((s, i) => s + i.quantity, 0);

  const activePriceTypes = useMemo(
    () => ALL_PRICE_TYPES.filter((pt) => priceTypeConfig[pt.configKey]),
    [priceTypeConfig],
  );

  useEffect(() => {
    if (activePriceTypes.length > 0) {
      const currentIsStillValid = activePriceTypes.some(
        (pt) => pt.field === priceType,
      );
      if (!currentIsStillValid) setPriceType(activePriceTypes[0].field);
    }
  }, [activePriceTypes, priceType]);

  const groupedProducts = useMemo(() => {
    let filtered = pricelists;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          (item.productName && item.productName.toLowerCase().includes(q)) ||
          (item.itemCode && item.itemCode.toLowerCase().includes(q)) ||
          (item.barCode && item.barCode.toLowerCase().includes(q)),
      );
    }
    if (advancedFilters) {
      if (advancedFilters.categories?.length > 0)
        filtered = filtered.filter((item) =>
          advancedFilters.categories.includes(item.categoryName),
        );
      if (advancedFilters.brands?.length > 0)
        filtered = filtered.filter((item) =>
          advancedFilters.brands.includes(item.brandName),
        );
      if (advancedFilters.priceRange && priceType) {
        const [min, max] = advancedFilters.priceRange;
        filtered = filtered.filter((item) => {
          const price = parseFloat(item[priceType]) || 0;
          return price >= min && price <= max;
        });
      }
    }
    return Object.values(
      filtered.reduce((acc, item) => {
        if (!acc[item.productId]) acc[item.productId] = [];
        acc[item.productId].push(item);
        return acc;
      }, {}),
    );
  }, [pricelists, searchQuery, advancedFilters, priceType]);

  // ── FETCHES ───────────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchBaseData = async () => {
      setIsFetchingBase(true);
      try {
        const [custRes, locRes] = await Promise.all([
          axios.get(`${domain}/api/Customers`),
          axios.get(`${domain}/api/Locations`),
        ]);
        setCustomers(custRes.data);
        setLocations(locRes.data);
      } catch {
        toast.error("Failed to load base form data.");
      } finally {
        setIsFetchingBase(false);
      }
    };
    fetchBaseData();
  }, []);

  const fetchPriceConfig = useCallback(
    async (locationId) => {
      if (!locationId) return;
      setIsConfigLoading(true);
      try {
        const res = await axios.get(
          `${domain}/api/PersonalizedPricing?locationId=${locationId}`,
        );
        const configs = res.data;
        let matched = null;
        if (Array.isArray(configs) && configs.length > 0) {
          matched =
            configs.find((c) => c.locationId === locationId) ||
            configs.find((c) => !c.locationId) ||
            configs[0];
        }
        if (matched) {
          dispatch(
            SET_PriceTypeConfig({
              showVatInc: matched.showVatInc,
              showVatEx: matched.showVatEx,
              showReseller: matched.showReseller,
              showZeroRated: matched.showZeroRated,
              configName: matched.configName,
            }),
          );
        }
      } catch {
        // Silently fail
      } finally {
        setIsConfigLoading(false);
      }
    },
    [dispatch],
  );

  useEffect(() => {
    if (!selectedLocationId) {
      setPricelists([]);
      return;
    }
    const fetchProducts = async () => {
      setIsLoadingProducts(true);
      try {
        const res = await axios.get(
          `${domain}/api/Products/pos-pricelist-by-location?locationId=${selectedLocationId}`,
        );
        setPricelists(res.data);
      } catch {
        toast.error("Failed to load products.");
        setPricelists([]);
      } finally {
        setIsLoadingProducts(false);
      }
    };
    fetchProducts();
  }, [selectedLocationId, isEditMode]);

  // Recalculate totals
  useEffect(() => {
    const { grandTotal, vatAmt, ewtAmt, netOfVat } = calculateTotals(
      formData.quotationSalesProducts,
      formData.shippingCost,
      formData.hasVat,
      formData.hasEwt,
    );
    setFormData((prev) => ({
      ...prev,
      totalAmount: grandTotal,
      vatAmount: vatAmt,
      ewtAmount: ewtAmt,
      vatableAmount: netOfVat,
    }));
  }, [
    formData.quotationSalesProducts,
    formData.shippingCost,
    formData.hasVat,
    formData.hasEwt,
  ]);

  // ── HANDLERS – Step 1 ─────────────────────────────────────────────────────
  const handleLocationChange = (e) => {
    const id = Number(e.target.value);
    const loc = locations.find((l) => l.id === id);
    if (selectedItems.length > 0) {
      if (
        !window.confirm(
          "Changing location will clear your selection. Continue?",
        )
      )
        return;
      setSelectedItems([]);
    }
    setSelectedLocationId(id);
    setSelectedLocationName(loc?.locationName || "");
    setSearchQuery("");
    fetchPriceConfig(id);
  };

  const handlePriceTypeChange = (e) => {
    const selected = e.target.value;
    if (selectedItems.length > 0) {
      if (
        !window.confirm(
          "Changing price type will clear your current selection. Continue?",
        )
      )
        return;
      setSelectedItems([]);
    }
    setPriceType(selected);
  };

  const handleAdd = useCallback(
    (product) => {
      if (!priceType) return toast.error("Please select a Price Type first.");
      const price = product[priceType];
      setSelectedItems((prev) => {
        const idx = prev.findIndex((i) => i.uniqueId === product.uniqueId);
        if (idx >= 0) {
          const updated = [...prev];
          updated[idx] = {
            ...updated[idx],
            quantity: updated[idx].quantity + 1,
            lineTotal: (updated[idx].quantity + 1) * price,
          };
          return updated;
        }
        return [
          ...prev,
          {
            uniqueId: product.uniqueId,
            productId: product.productId,
            productName: product.productName,
            itemCode: product.itemCode,
            quantity: 1,
            unitPrice: price,
            uomId: product.uomId,
            uom: product.uomName,
            conversionRate: product.conversionRate || 1,
            hasSerial: product.hasSerial,
            lineTotal: price,
            priceType: priceType,
          },
        ];
      });
    },
    [priceType],
  );

  const handleRemove = useCallback((product) => {
    setSelectedItems((prev) => {
      const idx = prev.findIndex((i) => i.uniqueId === product.uniqueId);
      if (idx < 0) return prev;
      if (prev[idx].quantity <= 1) return prev.filter((_, i) => i !== idx);
      const updated = [...prev];
      updated[idx] = {
        ...updated[idx],
        quantity: updated[idx].quantity - 1,
        lineTotal: (updated[idx].quantity - 1) * updated[idx].unitPrice,
      };
      return updated;
    });
  }, []);

  // ── STEP 1 → STEP 2 ───────────────────────────────────────────────────────
  const handleProceedToForm = () => {
    if (!selectedLocationId) return toast.warn("Select a location first.");
    if (!priceType) return toast.warn("Select a price type first.");
    if (selectedItems.length === 0)
      return toast.warn("Select at least one product.");

    const mappedItems = selectedItems.map((item, index) => ({
      ...item,
      lineNumber: index + 1,
      ...defaultDiscountFields,
    }));

    const autoHasVat = mappedItems.some(
      (item) => item.priceType === "vatInc" || item.priceType === "showVatInc",
    );

    setFormData((prev) => ({
      ...prev,
      locationId: selectedLocationId,
      locationName: selectedLocationName,
      quotationSalesProducts: mappedItems,
      hasVat: autoHasVat,
    }));
    setStep(2);
  };

  // ── HANDLERS – Step 2 ─────────────────────────────────────────────────────
  const handleCustomerChange = (e) => {
    const id = e.target.value;
    const customer = customers.find((c) => String(c.id) === id);
    setFormData((prev) => ({
      ...prev,
      customerId: id,
      customerName: customer?.customerName || "",
      shippingAddress: customer?.address || prev.shippingAddress,
      hasEwt: customer?.ewt || false,
    }));
  };

  const handleFinancialUpdate = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleLineItemChange = (index, field, value) => {
    const updatedItems = [...formData.quotationSalesProducts];
    const lineItem = { ...updatedItems[index], [field]: value };
    const qty =
      parseFloat(field === "quantity" ? value : lineItem.quantity) || 0;
    const unitPrice =
      parseFloat(field === "unitPrice" ? value : lineItem.unitPrice) || 0;
    const discountType =
      field === "discountType" ? value : lineItem.discountType || "%";
    const discountValue =
      field === "discountValue" ? value : lineItem.discountValue || 0;
    const { discountAmount, lineTotal } = computeDiscount(
      qty,
      unitPrice,
      discountType,
      discountValue,
    );
    lineItem.unitPrice = unitPrice;
    lineItem.discountType = discountType;
    lineItem.discountValue = discountValue;
    lineItem.discountAmount = discountAmount;
    lineItem.lineTotal = lineTotal;
    updatedItems[index] = lineItem;
    setFormData((prev) => ({ ...prev, quotationSalesProducts: updatedItems }));
  };

  const handleRemoveLineItem = (index) => {
    const itemToRemove = formData.quotationSalesProducts[index];

    // Remove from the order list
    setFormData((prev) => {
      const updatedItems = prev.quotationSalesProducts.filter((_, i) => i !== index);
      return { ...prev, quotationSalesProducts: updatedItems };
    });

    // Remove from `selectedItems` so Step 1 accurately reflects the state
    setSelectedItems((prev) =>
      prev.filter(
        (si) =>
          !(
            si.productId === itemToRemove.productId &&
            si.uomId === itemToRemove.uomId
          ),
      ),
    );
  };

  const handleSave = async () => {
    if (!formData.customerId) return toast.error("Please select a customer.");
    if (!formData.validityDate)
      return toast.error("Validity Date is required.");
    if (!formData.shippingAddress)
      return toast.error("Shipping Address is required.");

    const confirmMsg = isEditMode
      ? "Save changes to this Quotation Sales?"
      : "Create Quotation Sales?";
    if (!window.confirm(confirmMsg)) return;

    setIsLoading(true);
    try {
      const finalItems = formData.quotationSalesProducts.map((item) => ({
        productId: item.productId,
        quantity: parseFloat(item.quantity) || 0,
        unitPrice: parseFloat(item.unitPrice) || 0,
        lineTotal: parseFloat(item.lineTotal) || 0,
        uomId: item.uomId,
        conversionRate: item.conversionRate || 1,
        priceType: item.priceType,
        discountType: item.discountType || "%",
        discountValue: parseFloat(item.discountValue) || 0,
        discountAmount: parseFloat(item.discountAmount) || 0,
      }));

      const payload = {
        ...formData,
        customerId: parseInt(formData.customerId) || null,
        locationId: parseInt(formData.locationId) || null,
        totalAmount: parseFloat(formData.totalAmount) || 0,
        shippingCost: parseFloat(formData.shippingCost) || 0,
        vatAmount: formData.hasVat ? parseFloat(formData.vatAmount) || 0 : 0,
        ewtAmount: formData.hasEwt ? parseFloat(formData.ewtAmount) || 0 : 0,
        vatableAmount: parseFloat(formData.vatableAmount) || 0,
        quotationSalesProducts: finalItems,
      };

      if (isEditMode) {
        await axios.put(`${domain}/api/QuotationSales/${qsData.id}`, payload);
        toast.success("Quotation Sales updated successfully!");
      } else {
        await axios.post(`${domain}/api/QuotationSales`, payload);
        toast.success("Quotation Sales created successfully!");
      }

      if (refreshData) refreshData();
      onClose();
    } catch (error) {
      console.error(error);
      toast.error(
        error.response?.data?.message ||
          (isEditMode
            ? "Failed to update Quotation Sales."
            : "Failed to create Quotation Sales."),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const rawSubtotal = useMemo(
    () =>
      calculateTotals(formData.quotationSalesProducts, 0, false, false)
        .subtotal,
    [formData.quotationSalesProducts],
  );

  const headerTitle = isEditMode
    ? `Edit ${qsData.quoteNumber}`
    : step === 1
      ? "Product Selection"
      : "Quotation Details";

  const headerSubtitle = isEditMode
    ? "Modify quotation details and quantities"
    : step === 1
      ? "Select products to quote"
      : "Review and finalize quotation";


  // added by JV - start - 20260602
  const [showAddProductForm, setShowAddProductForm] = useState(false);
  const openAddProductForm = () => {
    setShowAddProductForm(true);
  }
  const closeAddProductForm = () => {
    setShowAddProductForm(false);
  }

  const handleAddProduct = async () => {
    openAddProductForm();
  }

  const [searchTerm, setSearchTerm] = useState("");

  // Filter the grouped products based on the search term
  const filteredProducts = groupedProducts.filter((group) => {
    const firstItem = group[0];

    return (
      firstItem.productName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      firstItem.itemCode.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  const handleSubmitAddProduct = () => {
    if (selectedItems.length === 0)
      return toast.warn("Select at least one product.");

    const mappedItems = selectedItems.map((item, index) => ({
      ...item,
      lineNumber: index + 1,
      ...defaultDiscountFields,
    }));

    const autoHasVat = mappedItems.some(
      (item) => item.priceType === "vatInc" || item.priceType === "showVatInc",
    );

    setFormData((prev) => ({
      ...prev,
      locationId: selectedLocationId,
      locationName: selectedLocationName,
      quotationSalesProducts: mappedItems,
      hasVat: autoHasVat,
    }));
    closeAddProductForm();
  };

  useEffect(() => {
    if (isEditMode && formData.quotationSalesProducts && qsData) {
      setSelectedLocationId(qsData.locationId);
      setPriceType(qsData.hasVat ? "vatInc" : "")

      const initializedItems = formData.quotationSalesProducts.map((item) => {
        const productData = pricelists.find(i => i.productId?.toString() === item.productId?.toString())
        return {
          ...item,
          uniqueId: productData?.uniqueId,
          // Ensure these are numbers for the .reduce() calculation in ProductRow
          quantity: parseFloat(item.quantity) || 0,
          unitPrice: parseFloat(item.unitPrice) || parseFloat(item.price) || 0,
          lineTotal: parseFloat(item.lineTotal) || 0,
        };
      });

      setSelectedItems(initializedItems);
    }
  }, [isEditMode, qsData, formData.quotationSalesProducts, pricelists]);

  
  const [terms, setTerms] = useState(null)
  const [salesmans, setSalesmans] = useState([])

  useEffect(() => {
    if(formData.customerId){
      const foundCustomer = customers.find(i => i.id.toString() === formData.customerId?.toString())

      setTerms(foundCustomer?.termsId ?? null)
      setSalesmans(foundCustomer?.salesmans ?? [])
    }
  }, [formData.customerId, customers])

  const prevCustomerId = useRef(formData.customerId);

  useEffect(() => {
    if (
      prevCustomerId.current !== undefined &&
      prevCustomerId.current !== formData.customerId
    ) {
      setFormData(prev => ({
        ...prev,
        salesmanId: "",
      }));
    }

    prevCustomerId.current = formData.customerId;
  }, [formData.customerId]);
  // added by JV - end - 20260602

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-gray-900/60 backdrop-blur-sm p-4">
      <div className="flex h-full max-h-[95vh] w-full max-w-6xl flex-col rounded-2xl bg-white shadow-2xl overflow-hidden border border-gray-200">
        {/* HEADER */}
        <div className="flex items-center justify-between border-b border-gray-100 bg-white px-6 py-4">
          <div className="flex items-center gap-4">
            <div
              className={`flex h-10 w-10 items-center justify-center rounded-xl text-white shadow-lg transition-all ${
                isEditMode
                  ? "bg-amber-500 shadow-amber-200"
                  : step === 1
                    ? "bg-teal-600 shadow-teal-200"
                    : "bg-green-600 shadow-green-200"
              }`}
            >
              {isEditMode ? (
                <FaEdit size={18} />
              ) : step === 1 ? (
                <FaBoxOpen size={18} />
              ) : (
                <FaFileAlt size={18} />
              )}
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900 leading-none">
                {headerTitle}
              </h2>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1">
                {headerSubtitle}
              </p>
            </div>
          </div>
          <div className="hidden items-center gap-4">
            <StepIndicator step={step} isEditMode={isEditMode} />
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-red-500 transition-colors bg-gray-50 hover:bg-red-50 rounded-lg"
            >
              <FaTimes size={18} />
            </button>
          </div>
        </div>

        {/* BODY */}
        <div className="flex-grow overflow-hidden flex flex-col bg-gray-50/30">
          {!isEditMode &&
          step === 1 &&
          (isFetchingBase || isLoadingProducts || isConfigLoading) ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="flex flex-col items-center gap-3">
                <Loader2 size={32} className="text-teal-500 animate-spin" />
                <p className="text-sm text-gray-500 font-medium">Loading...</p>
              </div>
            </div>
          ) : (
            <div
              className={`flex-1 overflow-hidden ${
                !isEditMode && step === 1 ? "flex flex-col" : "overflow-y-auto"
              } px-6 py-6`}
            >
              {/* ── STEP 1 ─────────────────────────────────────────────── */}
              {!isEditMode && step === 1 && (
                <div className="flex flex-col flex-1 overflow-hidden gap-4">
                  {/* Control Bar */}
                  <div className="flex flex-col gap-3 shrink-0">
                    <div className="flex flex-col lg:flex-row gap-3">
                      <div className="flex gap-2 flex-1">
                        <div className="relative flex-1 group">
                          <Search
                            className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-teal-500 transition-colors"
                            size={16}
                          />
                          <input
                            type="text"
                            className="w-full pl-10 pr-4 py-2.5 text-sm font-medium border border-gray-200 rounded-xl bg-white placeholder-gray-400 focus:outline-none focus:ring-4 focus:ring-teal-500/10 focus:border-teal-500 hover:border-gray-300 transition-all shadow-sm"
                            placeholder="Search products by name, code, or barcode..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            disabled={!selectedLocationId || isLoadingProducts}
                          />
                        </div>
                        <button
                          onClick={() => setIsAdvancedSearchOpen(true)}
                          disabled={
                            !selectedLocationId ||
                            isLoadingProducts ||
                            pricelists.length === 0
                          }
                          className="relative flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border border-gray-200 rounded-xl bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-300 focus:outline-none transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <SlidersHorizontal
                            size={16}
                            className="text-gray-400"
                          />
                          <span className="hidden sm:inline">Filters</span>
                        </button>
                      </div>

                      <div className="flex flex-col sm:flex-row gap-3 lg:w-auto w-full">
                        <div className="relative w-full sm:w-64 group">
                          <MapPin
                            className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-teal-500 transition-colors z-10"
                            size={16}
                          />
                          <select
                            className={`w-full pl-10 pr-10 py-2.5 text-sm font-semibold rounded-xl focus:outline-none focus:ring-4 shadow-sm appearance-none transition-all cursor-pointer border ${
                              !selectedLocationId
                                ? "border-amber-300 text-amber-800 bg-amber-50 focus:ring-amber-500/20"
                                : "border-gray-200 text-gray-800 bg-white focus:ring-teal-500/10 focus:border-teal-500 hover:border-gray-300"
                            }`}
                            value={selectedLocationId}
                            onChange={handleLocationChange}
                          >
                            <option value="" disabled>
                              Select Location...
                            </option>
                            {locations.map((loc) => (
                              <option key={loc.id} value={loc.id}>
                                {loc.locationName}
                              </option>
                            ))}
                          </select>
                          <ChevronDown
                            size={14}
                            className="absolute right-3.5 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400"
                          />
                        </div>

                        <div className="relative w-full sm:w-56 group">
                          <select
                            className={`w-full px-4 pr-10 py-2.5 text-sm font-semibold rounded-xl focus:outline-none focus:ring-4 shadow-sm appearance-none transition-all cursor-pointer border ${
                              !priceType
                                ? "border-amber-300 text-amber-800 bg-amber-50"
                                : "border-gray-200 text-gray-800 bg-white focus:ring-teal-500/10 focus:border-teal-500 hover:border-gray-300"
                            }`}
                            value={priceType}
                            onChange={handlePriceTypeChange}
                            disabled={activePriceTypes.length === 0}
                          >
                            <option value="" disabled>
                              Select Price Type...
                            </option>
                            {activePriceTypes.map((pt) => (
                              <option key={pt.field} value={pt.field}>
                                {pt.fullLabel}
                              </option>
                            ))}
                          </select>
                          <ChevronDown
                            size={14}
                            className="absolute right-3.5 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400"
                          />
                        </div>
                      </div>
                    </div>

                    {priceTypeConfig.configName && selectedLocationId && (
                      <div className="flex items-center gap-2 text-xs text-teal-700 bg-teal-50 border border-teal-100 px-3 py-1.5 rounded-lg w-fit">
                        <Settings2 size={13} />
                        <span className="font-bold">
                          Active Config: {priceTypeConfig.configName}
                        </span>
                        <span className="text-teal-300">|</span>
                        <span className="font-medium text-teal-500">
                          {activePriceTypes.map((pt) => pt.label).join(", ")}
                        </span>
                      </div>
                    )}
                  </div>

                  {(!selectedLocationId || !priceType) && (
                    <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 shrink-0">
                      <AlertCircle
                        className="text-amber-500 shrink-0"
                        size={16}
                      />
                      <p className="text-sm text-amber-800 font-semibold">
                        {!selectedLocationId && !priceType
                          ? "Please select a location and price type to begin."
                          : !selectedLocationId
                            ? "Please select a location to load products."
                            : "Please select a price type to continue."}
                      </p>
                    </div>
                  )}

                  {selectedLocationId && priceType && (
                    <>
                      <div className="md:hidden space-y-2 overflow-y-auto flex-1 pb-2">
                        {groupedProducts.map((group) => (
                          <ProductRow
                            key={group[0].productId}
                            group={group}
                            selectedItems={selectedItems}
                            onAdd={handleAdd}
                            onRemove={handleRemove}
                            activePriceTypes={activePriceTypes}
                            priceType={priceType}
                            isMobile={true}
                          />
                        ))}
                      </div>
                      <div className="hidden md:block flex-1 overflow-auto rounded-xl border border-gray-200 shadow-sm">
                        <table className="w-full bg-white">
                          <thead className="sticky top-0 z-10">
                            <tr className="bg-gray-50 border-b border-gray-200">
                              <th className="py-2 px-6 text-xs font-black text-gray-400 text-left uppercase">
                                Product
                              </th>
                              <th className="py-2 px-6 text-xs font-black text-gray-400 text-center uppercase">
                                Unit
                              </th>
                              <th className="py-2 px-6 text-xs font-black text-gray-400 text-center uppercase">
                                Type
                              </th>
                              {activePriceTypes.map((pt) => (
                                <th
                                  key={pt.field}
                                  className={`py-2 px-3 text-xs font-black text-center uppercase ${pt.field === priceType ? pt.activeHeaderClass : "text-gray-400"}`}
                                >
                                  {pt.label}
                                  {pt.field === priceType && (
                                    <span className="ml-1 text-[9px] normal-case font-normal opacity-70">
                                      ← active
                                    </span>
                                  )}
                                </th>
                              ))}
                              <th className="py-2 px-6 text-xs font-black text-gray-400 text-center uppercase">
                                Stock
                              </th>
                              <th className="py-2 px-6 text-xs font-black text-gray-400 text-center uppercase">
                                Add
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {groupedProducts.map((group) => (
                              <ProductRow
                                key={group[0].productId}
                                group={group}
                                selectedItems={selectedItems}
                                onAdd={handleAdd}
                                onRemove={handleRemove}
                                activePriceTypes={activePriceTypes}
                                priceType={priceType}
                                isMobile={false}
                              />
                            ))}
                            {groupedProducts.length === 0 && (
                              <tr>
                                <td
                                  colSpan={5 + activePriceTypes.length}
                                  className="py-16 text-center text-gray-400 text-sm"
                                >
                                  No products found.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* ── STEP 2 / EDIT MODE ─────────────────────────────────── */}
              {(step === 2 || isEditMode) && (
                <div className="space-y-6">
                  {isEditMode && (
                    <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
                      <FaEdit className="text-amber-500 shrink-0" size={14} />
                      <p className="text-sm text-amber-800 font-semibold">
                        You are editing{" "}
                        <span className="font-black">{qsData.quoteNumber}</span>
                        . Changes will overwrite the existing quotation.
                      </p>
                    </div>
                  )}

                  {/* Details Card */}
                  <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                      <div className="hidden md:col-span-1 space-y-3 md:border-r border-gray-100 md:pr-6">
                        <div>
                          <label className={labelClass}>Items Count</label>
                          <div className={readOnlyBox}>
                            <Package className="text-teal-500" size={14} />
                            <span className="text-sm font-bold text-teal-700">
                              {formData.quotationSalesProducts.length} Products
                            </span>
                          </div>
                        </div>
                        <div>
                          <label className={labelClass}>Stock Location</label>
                          <div className={readOnlyBox}>
                            <FaMapMarkerAlt
                              className="text-slate-400"
                              size={14}
                            />
                            <span className="text-sm font-semibold text-gray-600 truncate">
                              {formData.locationName}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="md:col-span-3">
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                          <div className="sm:col-span-2">
                            <label className={labelClass}>
                              Customer <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                              <FaUser
                                size={12}
                                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                              />
                              <select
                                value={formData.customerId}
                                onChange={handleCustomerChange}
                                className={`${inputClass} pl-8 appearance-none`}
                              >
                                <option value="">— Select Customer —</option>
                                {customers.map((c) => (
                                  <option key={c.id} value={String(c.id)}>
                                    {c.customerName}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>

                          <div className="sm:col-span-1">
                            <label className={labelClass}>
                              Salesman <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                              <FaUser
                                size={12}
                                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                              />
                              <select
                                value={formData.salesmanId}
                                onChange={(e) =>
                                  setFormData({
                                    ...formData,
                                    salesmanId: e.target.value,
                                  })
                                }
                                className={`${inputClass} pl-8 appearance-none`}
                              >
                                <option value="">— Select Salesman —</option>
                                {salesmans.map((c) => (
                                  <option key={c.id} value={String(c.id)}>
                                    {c.name}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>

                          <div>
                            <label className={labelClass}>Quote Date</label>
                            <input
                              type="date"
                              value={formData.date}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  date: e.target.value,
                                })
                              }
                              className={inputClass}
                            />
                          </div>

                          <div>
                            <label className={labelClass}>
                              Validity Date{" "}
                              <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="date"
                              value={formData.validityDate}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  validityDate: e.target.value,
                                })
                              }
                              className={`${inputClass} !border-teal-300`}
                            />
                          </div>

                          <div className="sm:col-span-3">
                            <label className={labelClass}>
                              Shipping Address{" "}
                              <span className="text-red-500">*</span>
                            </label>
                            <textarea
                              rows={1}
                              value={formData.shippingAddress}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  shippingAddress: e.target.value,
                                })
                              }
                              placeholder="Destination address..."
                              className={inputClass}
                            />
                          </div>

                          <div>
                            <label className={labelClass}>
                              Terms
                            </label>
                            <TermsSelect data={terms} setData={() => {}} isEditable={false}/>
                          </div>

                          <div className="sm:col-span-3">
                            <label className={labelClass}>
                              Internal Remarks
                            </label>
                            <textarea
                              rows={1}
                              value={formData.remarks}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  remarks: e.target.value,
                                })
                              }
                              placeholder="Optional notes..."
                              className={inputClass}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col gap-3 shrink-0">
                    <div className="flex flex-col lg:flex-row gap-3">
                      <div className="flex flex-col sm:flex-row gap-3 lg:w-auto w-full">
                        <button
                          onClick={() => setIsAdvancedSearchOpen(true)}
                          disabled={
                            !selectedLocationId ||
                            isLoadingProducts ||
                            pricelists.length === 0
                          }
                          className="relative flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border border-gray-200 rounded-xl bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-300 focus:outline-none transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <SlidersHorizontal
                            size={16}
                            className="text-gray-400"
                          />
                          <span className="hidden sm:inline">Filters</span>
                        </button>
                        <div className="relative w-full sm:w-64 group">
                          <MapPin
                            className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-teal-500 transition-colors z-10"
                            size={16}
                          />
                          <select
                            className={`w-full pl-10 pr-10 py-2.5 text-sm font-semibold rounded-xl focus:outline-none focus:ring-4 shadow-sm appearance-none transition-all cursor-pointer border ${
                              !selectedLocationId
                                ? "border-amber-300 text-amber-800 bg-amber-50 focus:ring-amber-500/20"
                                : "border-gray-200 text-gray-800 bg-white focus:ring-teal-500/10 focus:border-teal-500 hover:border-gray-300"
                            }`}
                            value={selectedLocationId}
                            onChange={handleLocationChange}
                          >
                            <option value="" disabled>
                              Select Location...
                            </option>
                            {locations.map((loc) => (
                              <option key={loc.id} value={loc.id}>
                                {loc.locationName}
                              </option>
                            ))}
                          </select>
                          <ChevronDown
                            size={14}
                            className="absolute right-3.5 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400"
                          />
                        </div>

                        <div className="relative w-full sm:w-56 group">
                          <select
                            className={`w-full px-4 pr-10 py-2.5 text-sm font-semibold rounded-xl focus:outline-none focus:ring-4 shadow-sm appearance-none transition-all cursor-pointer border ${
                              !priceType
                                ? "border-amber-300 text-amber-800 bg-amber-50"
                                : "border-gray-200 text-gray-800 bg-white focus:ring-teal-500/10 focus:border-teal-500 hover:border-gray-300"
                            }`}
                            value={priceType}
                            onChange={handlePriceTypeChange}
                            disabled={activePriceTypes.length === 0}
                          >
                            <option value="" disabled>
                              Select Price Type...
                            </option>
                            {activePriceTypes.map((pt) => (
                              <option key={pt.field} value={pt.field}>
                                {pt.fullLabel}
                              </option>
                            ))}
                          </select>
                          <ChevronDown
                            size={14}
                            className="absolute right-3.5 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400"
                          />
                        </div>
                      </div>
                    </div>

                    {priceTypeConfig.configName && selectedLocationId && (
                      <div className="flex items-center gap-2 text-xs text-teal-700 bg-teal-50 border border-teal-100 px-3 py-1.5 rounded-lg w-fit">
                        <Settings2 size={13} />
                        <span className="font-bold">
                          Active Config: {priceTypeConfig.configName}
                        </span>
                        <span className="text-teal-300">|</span>
                        <span className="font-medium text-teal-500">
                          {activePriceTypes.map((pt) => pt.label).join(", ")}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Line Items Table */}
                  <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                    <table className="min-w-full divide-y divide-gray-100">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-2 text-left text-[10px] font-black uppercase text-gray-400">Product</th>
                          <th className="px-6 py-2 text-center text-[10px] font-black uppercase text-gray-400">Brand</th>
                          <th className="px-6 py-2 text-center text-[10px] font-black uppercase text-gray-400 w-24">Stocks</th>
                          <th className="px-6 py-2 text-center text-[10px] font-black uppercase text-gray-400 w-32">Qty</th>
                          <th className="px-6 py-2 text-right text-[10px] font-black uppercase text-gray-400 w-48">Unit Price</th>
                          <th className="px-4 py-2 text-center text-[10px] font-black uppercase text-gray-400 w-24">
                            <span className="flex items-center justify-center gap-1">
                              <FaPercentage size={9} /> Discount
                            </span>
                          </th>
                          <th className="px-6 py-2 text-right text-[10px] font-black uppercase text-gray-400 w-32">Total</th>
                          <th className="px-6 py-2 text-right text-[10px] font-black uppercase text-gray-400 w-24">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {formData.quotationSalesProducts.length > 0 ? (
                          <>
                            {formData.quotationSalesProducts.map((item, index) => {
                              const hasDiscount = (parseFloat(item.discountValue) || 0) > 0;
                              const foundPricelist = pricelists?.find(i => i.productId === item.productId)

                              const isGoodsItem = !serviceCategories.includes(foundPricelist?.categoryId)
                              return (
                                <tr key={index} className={`hover:bg-gray-50/50 transition-colors ${hasDiscount ? "bg-rose-50/20" : ""}`}>
                                  {/* ... your existing <td> cells ... */}
                                  <td className="px-6 py-3">
                                      <p className="text-sm font-bold text-gray-800">{item.productName}</p>
                                      <p className="text-[10px] font-bold text-teal-500 uppercase">{item.priceType} · {item.uom}</p>
                                  </td>
                                  <td className="py-2 px-6 text-center">
                                    {foundPricelist.brandName ?
                                    <>
                                      <span className="text-xs">{foundPricelist.brandName}</span>
                                    </>
                                    :
                                    <>
                                      <span className="text-xs italic text-gray-400">{"N/A"}</span>
                                    </>
                                    }
                                  </td>
                                  <td className="px-6 py-3">
                                    <p className="text-sm font-bold text-gray-500">{`${isGoodsItem ? (foundPricelist.stockCount ?? "0") : "-"}`}</p>
                                  </td>
                                  <td className="px-6 py-3">
                                      <input disabled={!isGoodsItem} type="number" value={item.quantity} onChange={(e) => handleLineItemChange(index, "quantity", e.target.value)} className="w-full rounded-md border border-gray-300 bg-white py-1.5 text-center text-sm font-bold focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all hover:border-gray-400" />
                                  </td>
                                  <td className="px-6 py-3">
                                      <div className="relative flex items-center group">
                                          <span className="absolute left-3 text-[10px] font-black text-gray-400 group-focus-within:text-teal-500 pointer-events-none">₱</span>
                                          <input type="number" value={item.unitPrice} onChange={(e) => handleLineItemChange(index, "unitPrice", e.target.value)} className="w-full rounded-lg border border-gray-200 bg-white pl-7 pr-3 py-1.5 text-right text-sm font-bold text-gray-700 shadow-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all" />
                                      </div>
                                  </td>
                                  <td className="px-4 py-3">
                                      <DiscountCell item={item} index={index} onLineItemChange={handleLineItemChange} />
                                  </td>
                                  <td className="px-6 py-3 text-right">
                                      <div className="flex flex-col items-end">
                                          {hasDiscount && <span className="text-[9px] text-gray-400 line-through font-medium">{fmt((parseFloat(item.quantity) || 0) * (parseFloat(item.unitPrice) || 0))}</span>}
                                          <span className={`text-sm font-bold ${hasDiscount ? "text-rose-700" : "text-gray-900"}`}>{fmt(item.lineTotal)}</span>
                                      </div>
                                  </td>
                                  <td className="px-3 py-4 text-center">
                                    <button
                                      type="button"
                                      onClick={() => handleRemoveLineItem(index)}
                                      className="p-2 text-red-500 hover:text-red-700 rounded-full hover:bg-red-50"
                                      title="Remove item"
                                    >
                                      <FaTrash />
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                            {/* Action Row: Shown when items exist */}
                            <tr>
                              <td colSpan={8} className="px-6 py-3 bg-gray-50/30">
                                <button
                                  type="button"
                                  disabled={
                                    !selectedLocationId ||
                                    isLoadingProducts ||
                                    pricelists.length === 0
                                  }
                                  onClick={handleAddProduct}
                                  className="flex items-center gap-2 text-sm font-bold text-teal-600 transition-all 
                                    enabled:hover:text-teal-700 
                                    disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  <div className="flex h-5 w-5 items-center justify-center rounded-full bg-teal-100">
                                    <span className="text-lg">+</span>
                                  </div>
                                  Add another product
                                </button>
                                {(!selectedLocationId || isLoadingProducts || pricelists.length === 0) && (
                                  <div className="inline-flex items-center gap-2 rounded-full bg-amber-50 px-3 py-1 border border-amber-100">
                                    <div className="flex h-2 w-2 rounded-full bg-amber-400 animate-pulse" />
                                    <span className="text-[11px] font-bold uppercase tracking-wider text-amber-700">
                                      {isLoadingProducts 
                                        ? "Syncing Products..." 
                                        : !selectedLocationId 
                                          ? "Select location to unlock" 
                                          : "No pricelists available. (Go to Prices > Selling Price)"}
                                    </span>
                                  </div>
                                )}
                              </td>
                            </tr>
                          </>
                        ) : (
                          /* Empty State Row */
                          <tr>
                            <td colSpan={8} className="px-6 py-16 text-center">
                              <div className="flex flex-col items-center justify-center gap-3">
                                <div className="rounded-full bg-gray-50 p-4">
                                  <svg className="w-8 h-8 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                  </svg>
                                </div>
                                <div className="text-sm font-medium text-gray-500">No products added yet.</div>
                                <button
                                  type="button"
                                  disabled={!selectedLocationId || isLoadingProducts || pricelists.length === 0}
                                  onClick={handleAddProduct}
                                  className="mt-2 rounded-lg bg-teal-600 px-4 py-2 text-sm font-bold text-white shadow-md shadow-teal-500/20 transition-all 
                                    enabled:hover:bg-teal-700 enabled:active:scale-95 
                                    disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none"
                                >
                                  Add Your First Product
                                </button>
                                {(!selectedLocationId || isLoadingProducts || pricelists.length === 0) && (
                                  <div className="inline-flex items-center gap-2 rounded-full bg-amber-50 px-3 py-1 border border-amber-100">
                                    <div className="flex h-2 w-2 rounded-full bg-amber-400 animate-pulse" />
                                    <span className="text-[11px] font-bold uppercase tracking-wider text-amber-700">
                                      {isLoadingProducts 
                                        ? "Syncing Products..." 
                                        : !selectedLocationId 
                                          ? "Select location to unlock" 
                                          : "No pricelists available. (Go to Prices > Selling Price)"}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  <OrderFinancialSummary
                    subtotal={rawSubtotal}
                    shippingCost={formData.shippingCost}
                    hasVat={formData.hasVat}
                    hasEwt={formData.hasEwt}
                    vatAmount={formData.vatAmount}
                    ewtAmount={formData.ewtAmount}
                    vatableAmount={formData.vatableAmount}
                    totalAmount={formData.totalAmount}
                    onUpdate={handleFinancialUpdate}
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/* FOOTER */}
        <div className="flex items-center justify-end border-t border-gray-100 bg-white px-6 py-4 gap-4">
          {isEditMode ? (
            <>
              <button
                onClick={onClose}
                className="text-sm font-bold text-gray-400 hover:text-gray-600 transition-colors px-4 py-2"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={isLoading}
                className="flex items-center gap-2 rounded-xl bg-amber-500 px-8 py-3 text-sm font-black text-white shadow-lg shadow-amber-100 hover:bg-amber-600 transition-all active:scale-95 disabled:opacity-50"
              >
                {isLoading ? (
                  <Loader2 className="animate-spin" size={16} />
                ) : (
                  <FaSave size={13} />
                )}
                Save Changes
              </button>
            </>
          ) : step === 1 ? (
            <>
              <button
                onClick={onClose}
                className="text-sm font-bold text-gray-400 hover:text-gray-600 transition-colors px-4 py-2"
              >
                Cancel
              </button>
              <button
                onClick={handleProceedToForm}
                disabled={
                  selectedItems.length === 0 ||
                  !selectedLocationId ||
                  !priceType
                }
                className="flex items-center gap-2 rounded-xl bg-teal-600 px-8 py-3 text-sm font-black text-white shadow-lg shadow-teal-100 hover:bg-teal-700 transition-all active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next Step
                {totalSelectedQty > 0 && (
                  <span className="inline-flex items-center justify-center bg-white text-teal-600 text-xs font-black rounded-full min-w-[22px] h-[22px] px-1.5 shadow-sm">
                    {totalSelectedQty}
                  </span>
                )}
                <FaArrowRight size={12} />
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => setStep(1)}
                className="hidden text-sm font-bold text-gray-400 hover:text-gray-600 transition-colors px-4 py-2"
              >
                ← Back to Selection
              </button>
              <button
                onClick={onClose}
                disabled={isLoading}
                className="flex items-center gap-2 rounded-xl bg-gray-300 px-8 py-3 text-sm font-black text-gray-600 shadow-lg shadow-teal-100 hover:bg-gray-400 transition-all active:scale-95 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={isLoading}
                className="flex items-center gap-2 rounded-xl bg-teal-600 px-8 py-3 text-sm font-black text-white shadow-lg shadow-teal-100 hover:bg-teal-700 transition-all active:scale-95 disabled:opacity-50"
              >
                {isLoading ? (
                  <Loader2 className="animate-spin" size={16} />
                ) : (
                  <FaSave size={13} />
                )}
                Create Quotation
              </button>
            </>
          )}
        </div>
      </div>

      <AddSalesOrderAdvancedSearch
        isOpen={isAdvancedSearchOpen}
        onClose={() => setIsAdvancedSearchOpen(false)}
        products={pricelists}
        activePriceType={priceType}
        appliedFilters={advancedFilters}
        onApplyFilters={setAdvancedFilters}
      />

      {showAddProductForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/60 backdrop-blur-sm p-4">
          <div className="w-full max-w-5xl rounded-2xl bg-white shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
            
            {/* Modal Header */}
            <div className="border-b border-gray-100 bg-gray-50/50 px-6 py-4">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-black text-gray-800">Select Products</h3>
                  <p className="text-xs text-gray-500 font-medium">Browse and add items to your quotation</p>
                </div>
                <button 
                  onClick={closeAddProductForm}
                  className="rounded-full p-2 hover:bg-gray-200 transition-colors"
                >
                  <FaTimes className="text-gray-400" size={18} />
                </button>
              </div>

              {/* Search Bar */}
              <div className="relative">
                <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                  <FaSearch className="text-gray-400" size={14} />
                </div>
                <input
                  type="text"
                  placeholder="Search by product name or item code..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none transition-all shadow-sm"
                />
              </div>
            </div>

            {/* Modal Body / Table */}
            <div className="flex-1 overflow-y-auto">
              <table className="min-w-full divide-y divide-gray-200 border-separate border-spacing-0">
                <thead className="bg-white sticky top-0 z-10 shadow-sm">
                  <tr>
                    <th className="px-6 py-3 text-left text-[10px] font-black uppercase text-gray-400">Product</th>
                    <th className="px-6 py-3 text-center text-[10px] font-black uppercase text-gray-400">Brand</th>
                    <th className="px-6 py-3 text-center text-[10px] font-black uppercase text-gray-400">Unit</th>
                    <th className="px-6 py-3 text-center text-[10px] font-black uppercase text-gray-400">Type</th>
                    <th className="px-6 py-3 text-center text-[10px] font-black uppercase text-gray-400">VAT Inc</th>
                    <th className="px-6 py-3 text-center text-[10px] font-black uppercase text-gray-400">Stock</th>
                    <th className="px-6 py-3 text-right text-[10px] font-black uppercase text-gray-400">Add</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredProducts.map((group) => (
                    <ProductRow
                      key={group[0].productId}
                      group={group}
                      selectedItems={selectedItems}
                      onAdd={handleAdd}
                      onRemove={handleRemove}
                      activePriceTypes={activePriceTypes}
                      priceType={priceType}
                      isMobile={false}
                    />
                  ))}
                  {filteredProducts.length === 0 && (
                    <tr>
                      <td colSpan={6} className="py-20 text-center">
                        <div className="flex flex-col items-center gap-2">
                          <div className="bg-gray-50 p-4 rounded-full">
                            <FaSearch className="text-gray-200" size={30} />
                          </div>
                          <p className="text-gray-400 text-sm font-medium italic">
                            No products found matching "{searchTerm}"
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Modal Footer */}
            <div className="border-t border-gray-100 bg-gray-50 px-6 py-4 flex items-center justify-between">
              <div className="text-sm">
                <span className="text-gray-500">Selected Items: </span>
                <span className="font-black text-teal-600">
                  {formData.quotationSalesProducts.length}
                </span>
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={closeAddProductForm}
                  className="px-6 py-2 text-sm font-bold text-gray-500 hover:text-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSubmitAddProduct} // In this workflow, "Save" just closes because adding is instant
                  className="bg-teal-600 hover:bg-teal-700 text-white px-8 py-2 rounded-xl text-sm font-bold shadow-lg shadow-teal-500/30 transition-all active:scale-95"
                >
                  Done & Review
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddQuotationSales;
